// frontend/src/components/CrearOrdenCompraModal.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import presupuestosService from '../services/presupuestosService';
import './CrearModal.css';

const CrearOrdenCompraModal = ({ onClose, onCreate, recursoPreseleccionado, presupuestoId }) => {
  const [presupuestos, setPresupuestos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [disponibilidad, setDisponibilidad] = useState(null);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [selectedRecurso, setSelectedRecurso] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    presupuesto: presupuestoId ? presupuestoId.toString() : '',
    area: '',
    partida: '',
    recurso: '',
    proveedor: '',
    cantidad: '',
    valor_unitario: ''
  });

  useEffect(() => {
    cargarPresupuestos();
    cargarProveedores();
  }, []);

  // Preseleccionar recurso si viene del presupuesto
  useEffect(() => {
    if (recursoPreseleccionado && presupuestoId) {
      const { recurso, partida, area } = recursoPreseleccionado;
      setFormData(prev => ({
        ...prev,
        presupuesto: presupuestoId.toString(),
        area: area.id.toString(),
        partida: partida.id.toString(),
        recurso: recurso.id.toString(),
        proveedor: recurso.proveedor ? recurso.proveedor.toString() : '',
        cantidad: '',
        valor_unitario: ''
      }));
      // Cargar disponibilidad del recurso
      cargarDisponibilidad(recurso.id);
      // Precargar los datos en cascada
      setAreas([area]);
      setPartidas([partida]);
      setRecursos([recurso]);
      // Preseleccionar proveedor si el recurso tiene uno
      if (recurso.proveedor) {
        const prov = proveedores.find(p => p.id === recurso.proveedor);
        if (prov) {
          setSelectedProveedor(prov);
        }
      }
    }
  }, [recursoPreseleccionado, presupuestoId, proveedores]);

  const cargarPresupuestos = async () => {
    try {
      const response = await presupuestosService.getPresupuestos();
      let presupuestosData = [];

      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        presupuestosData = response.data.results;
      } else if (Array.isArray(response.data)) {
        presupuestosData = response.data;
      }

      // Filtrar solo presupuestos aprobados
      const aprobados = presupuestosData.filter(p => p.status === 'aprobado');
      setPresupuestos(aprobados);
    } catch (error) {
      console.error('Error al cargar presupuestos:', error);
      toast.error('Error al cargar presupuestos');
    }
  };

  const cargarProveedores = async () => {
    try {
      const response = await presupuestosService.getProveedores();
      let proveedoresData = [];

      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        proveedoresData = response.data.results;
      } else if (Array.isArray(response.data)) {
        proveedoresData = response.data;
      }

      setProveedores(proveedoresData);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      // No mostrar error para proveedores ya que es opcional
    }
  };

  const handlePresupuestoChange = (e) => {
    const presupuestoId = e.target.value;
    setFormData({
      presupuesto: presupuestoId,
      area: '',
      partida: '',
      recurso: '',
      cantidad: '',
      valor_unitario: ''
    });

    if (presupuestoId) {
      const presupuesto = presupuestos.find(p => p.id === parseInt(presupuestoId));
      setAreas(presupuesto?.areas || []);
      setPartidas([]);
      setRecursos([]);
      setDisponibilidad(null);
    } else {
      setAreas([]);
      setPartidas([]);
      setRecursos([]);
      setDisponibilidad(null);
    }
  };

  const handleAreaChange = (e) => {
    const areaId = e.target.value;
    setFormData({
      ...formData,
      area: areaId,
      partida: '',
      recurso: '',
      cantidad: '',
      valor_unitario: ''
    });

    if (areaId) {
      const area = areas.find(a => a.id === parseInt(areaId));
      setPartidas(area?.partidas || []);
      setRecursos([]);
      setDisponibilidad(null);
    } else {
      setPartidas([]);
      setRecursos([]);
      setDisponibilidad(null);
    }
  };

  const handlePartidaChange = (e) => {
    const partidaId = e.target.value;
    setFormData({
      ...formData,
      partida: partidaId,
      recurso: '',
      cantidad: '',
      valor_unitario: ''
    });

    if (partidaId) {
      const partida = partidas.find(p => p.id === parseInt(partidaId));
      console.log('📋 Partida seleccionada:', partida);
      const recursosPartida = partida?.recursos_estructura || partida?.recursos || [];
      console.log('🔧 Recursos de la partida:', recursosPartida);
      setRecursos(recursosPartida);
      setDisponibilidad(null);
    } else {
      setRecursos([]);
      setDisponibilidad(null);
    }
  };

  const cargarDisponibilidad = async (recursoId) => {
    if (recursoId) {
      try {
        const params = new URLSearchParams({
          recurso_id: recursoId,
          presupuesto_id: formData.presupuesto,
          partida_id: formData.partida
        });
        const response = await presupuestosService.getDisponibilidadRecurso(recursoId, params.toString());
        setDisponibilidad(response.data);
      } catch (error) {
        console.error('Error al obtener disponibilidad:', error);
        toast.error('Error al obtener disponibilidad del recurso');
      }
    } else {
      setDisponibilidad(null);
    }
  };

  const handleRecursoChange = async (e) => {
    const recursoId = e.target.value;
    setFormData({
      ...formData,
      recurso: recursoId,
      cantidad: '',
      valor_unitario: ''
    });

    if (recursoId) {
      // Encontrar el recurso seleccionado en la lista
      const recurso = recursos.find(r => r.id.toString() === recursoId);
      console.log('📦 Recurso seleccionado:', recurso);
      setSelectedRecurso(recurso || null);
      cargarDisponibilidad(recursoId);
    } else {
      setSelectedRecurso(null);
      setDisponibilidad(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Actualizar proveedor seleccionado cuando se cambia
    if (name === 'proveedor') {
      const proveedor = proveedores.find(p => p.id === parseInt(value));
      setSelectedProveedor(proveedor || null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.presupuesto || !formData.recurso || !formData.cantidad || !formData.valor_unitario) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    const cantidad = parseFloat(formData.cantidad);
    if (cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    if (disponibilidad && cantidad > disponibilidad.cantidad_disponible) {
      toast.error(`La cantidad no puede exceder ${disponibilidad.cantidad_disponible}`);
      return;
    }

    setLoading(true);
    try {
      await presupuestosService.createOrdenCompra({
        presupuesto: parseInt(formData.presupuesto),
        recurso: parseInt(formData.recurso),
        proveedor: formData.proveedor ? parseInt(formData.proveedor) : null,
        cantidad: parseFloat(formData.cantidad),
        valor_unitario: parseFloat(formData.valor_unitario)
      });
      toast.success('Orden de compra creada exitosamente');
      onCreate();
      onClose();
    } catch (error) {
      console.error('Error al crear orden de compra:', error);
      const errorMsg = error.response?.data?.cantidad?.[0] || 'Error al crear orden de compra';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const monto = formData.cantidad && formData.valor_unitario
    ? (parseFloat(formData.cantidad) * parseFloat(formData.valor_unitario)).toLocaleString('es-CL')
    : '0';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Crear Orden de Compra</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Presupuesto Aprobado *</label>
            <select
              value={formData.presupuesto}
              onChange={handlePresupuestoChange}
              required
              disabled={!!recursoPreseleccionado}
            >
              <option value="">Seleccionar presupuesto...</option>
              {presupuestos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.nombre_presupuesto} ({p.equipo_nombre})
                </option>
              ))}
            </select>
          </div>

          {formData.presupuesto && (
            <div className="form-group">
              <label>Área *</label>
              <select
                value={formData.area}
                onChange={handleAreaChange}
                required
                disabled={!!recursoPreseleccionado}
              >
                <option value="">Seleccionar área...</option>
                {areas.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.nombre_area} (Total: ${a.total_area.toLocaleString('es-CL')})
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.area && (
            <div className="form-group">
              <label>Partida *</label>
              <select
                value={formData.partida}
                onChange={handlePartidaChange}
                required
                disabled={!!recursoPreseleccionado}
              >
                <option value="">Seleccionar partida...</option>
                {partidas.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nombre_partida} (Total: ${p.total_partida.toLocaleString('es-CL')})
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.partida && (
            <div className="form-group">
              <label>Recurso *</label>
              <select
                value={formData.recurso}
                onChange={handleRecursoChange}
                required
                disabled={!!recursoPreseleccionado}
              >
                <option value="">Seleccionar recurso...</option>
                {recursos.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.nombre || r.nombre_recurso} (Cant: {r.cantidad_presupuesto || r.cantidad} {r.unidad})
                  </option>
                ))}
              </select>
            </div>
          )}

          {disponibilidad && selectedRecurso && (
            <div className="item-card" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              <div className="item-header">
                <h4>{selectedRecurso.nombre || disponibilidad.recurso_nombre}</h4>
                <div className="item-stats">
                  <span className="stat-badge presupuestada">
                    Presupuestada: {parseFloat(selectedRecurso.cantidad_presupuestada_costo || disponibilidad.cantidad_presupuestada || 0).toLocaleString('es-CL')}
                  </span>
                  <span className="stat-badge ejecutada">
                    Solicitada OC: {disponibilidad.cantidad_en_ocs.toLocaleString('es-CL')}
                  </span>
                  <span className="stat-badge pendiente">
                    Disponible: {disponibilidad.cantidad_disponible.toLocaleString('es-CL')}
                  </span>
                </div>
              </div>

              <div className="item-details" style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                {selectedRecurso.precio_unitario_costo !== undefined && (
                  <div>Costo Unitario: <strong>${(parseFloat(selectedRecurso.precio_unitario_costo) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}</strong></div>
                )}
                {(selectedRecurso.subtotal_costo_partida !== undefined || selectedRecurso.subtotal_costo !== undefined) && (
                  <div>Total Presupuestado: <strong>${(parseFloat(selectedRecurso.subtotal_costo_partida || selectedRecurso.subtotal_costo) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}</strong></div>
                )}
              </div>
            </div>
          )}

          {formData.recurso && disponibilidad && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Proveedor</label>
                <select
                  name="proveedor"
                  value={formData.proveedor}
                  onChange={handleChange}
                >
                  <option value="">Sin proveedor</option>
                  {Array.isArray(proveedores) && proveedores.map(proveedor => (
                    <option key={proveedor.id} value={proveedor.id}>
                      {proveedor.nombre_proveedor}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProveedor && (
                <div className="proveedor-info-box">
                  <h4>📋 Información del Proveedor</h4>
                  <div className="info-grid">
                    {selectedProveedor.rut && (
                      <div className="info-item">
                        <span className="info-label">RUT:</span>
                        <span className="info-value">{selectedProveedor.rut}</span>
                      </div>
                    )}
                    {selectedProveedor.mail && (
                      <div className="info-item">
                        <span className="info-label">Mail Alternativo:</span>
                        <span className="info-value">{selectedProveedor.mail}</span>
                      </div>
                    )}
                    {selectedProveedor.email && (
                      <div className="info-item">
                        <span className="info-label">Email:</span>
                        <span className="info-value">{selectedProveedor.email}</span>
                      </div>
                    )}
                    {selectedProveedor.telefono && (
                      <div className="info-item">
                        <span className="info-label">Teléfono:</span>
                        <span className="info-value">{selectedProveedor.telefono}</span>
                      </div>
                    )}
                    {selectedProveedor.contacto && (
                      <div className="info-item">
                        <span className="info-label">Contacto:</span>
                        <span className="info-value">{selectedProveedor.contacto}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Cantidad Solicitada * (máximo: {disponibilidad.cantidad_disponible.toLocaleString('es-CL')})</label>
                <input
                  type="number"
                  name="cantidad"
                  value={formData.cantidad}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  max={disponibilidad.cantidad_disponible}
                  placeholder="0"
                  required
                />
              </div>

              <div className="form-group">
                <label>Valor Unitario *</label>
                <input
                  type="number"
                  name="valor_unitario"
                  value={formData.valor_unitario}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  required
                />
              </div>

              <div className="monto-display">
                <span>Monto Total:</span>
                <span className="monto-value">${monto}</span>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.presupuesto || !formData.recurso}
            >
              {loading ? 'Creando...' : 'Crear Orden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrearOrdenCompraModal;
